import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  warehouseMonitoringService,
  WarehouseMonitoringStats,
} from "../services/WarehouseMonitoringService";
import { WarehouseMultiSelectOption } from "../components/extranets/internal/WarehouseMultiSelectFilter";
import { buildMultiSelectFilterParam } from "../utils/warehouseFilterUtils";
import {
  fetchHotelNameMap,
  formatHotelCodeList,
  getHotelDisplayName,
  HotelNameMap,
} from "../utils/warehouseHotelNames";

interface UseWarehouseFilterOptionsResult {
  stats: WarehouseMonitoringStats | null;
  statsError: string | null;
  hotelNames: HotelNameMap;
  hotelOptions: WarehouseMultiSelectOption[];
  pmsOptions: WarehouseMultiSelectOption[];
  selectedHotelCodes: string[];
  selectedPms: string[];
  setSelectedHotelCodes: (values: string[]) => void;
  setSelectedPms: (values: string[]) => void;
  hotelCodeFilter?: string;
  pmsFilter?: string;
  getHotelDisplayName: (hotelCode: string) => string;
  formatHotelCodeList: (hotelCodes: string[]) => string;
  ensureHotelNames: (hotelCodes: string[]) => Promise<void>;
  reloadFilterOptions: () => Promise<void>;
}

export function useWarehouseFilterOptions(): UseWarehouseFilterOptionsResult {
  const [stats, setStats] = useState<WarehouseMonitoringStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [hotelNames, setHotelNames] = useState<HotelNameMap>({});
  const [selectedHotelCodes, setSelectedHotelCodes] = useState<string[]>([]);
  const [selectedPms, setSelectedPms] = useState<string[]>([]);
  const initializedRef = useRef(false);

  const ensureHotelNames = useCallback(async (hotelCodes: string[]) => {
    const nextMap = await fetchHotelNameMap(hotelCodes);
    if (Object.keys(nextMap).length === 0) {
      return;
    }
    setHotelNames(prev => ({ ...prev, ...nextMap }));
  }, []);

  const reloadFilterOptions = useCallback(async () => {
    let nextStats: WarehouseMonitoringStats;
    try {
      nextStats = await warehouseMonitoringService.getStats();
    } catch (err) {
      // Never leave the screen silently empty: the reason belongs on screen.
      setStatsError(
        err instanceof Error ? err.message : "Failed to load warehouse filter options",
      );
      throw err;
    }

    setStatsError(null);
    setStats(nextStats);

    const hotelCodes = nextStats.hotelBreakdown?.map(item => item.hotelCode) ?? [];
    const pmsValues = nextStats.pmsBreakdown?.map(item => item.pms) ?? [];

    await ensureHotelNames(hotelCodes);

    if (!initializedRef.current) {
      setSelectedHotelCodes(hotelCodes);
      setSelectedPms(pmsValues);
      initializedRef.current = true;
    } else {
      setSelectedHotelCodes(prev =>
        prev.filter(code => hotelCodes.includes(code)).length
          ? prev.filter(code => hotelCodes.includes(code))
          : hotelCodes,
      );
      setSelectedPms(prev =>
        prev.filter(pms => pmsValues.includes(pms)).length
          ? prev.filter(pms => pmsValues.includes(pms))
          : pmsValues,
      );
    }
  }, [ensureHotelNames]);

  useEffect(() => {
    // Surfaced through statsError; callers that await it still get the rejection.
    void reloadFilterOptions().catch(() => undefined);
  }, [reloadFilterOptions]);

  const hotelOptions = useMemo<WarehouseMultiSelectOption[]>(
    () =>
      (stats?.hotelBreakdown ?? [])
        .map(item => ({
          value: item.hotelCode,
          label: getHotelDisplayName(item.hotelCode, hotelNames),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [stats?.hotelBreakdown, hotelNames],
  );

  const pmsOptions = useMemo<WarehouseMultiSelectOption[]>(
    () =>
      (stats?.pmsBreakdown ?? [])
        .map(item => ({
          value: item.pms,
          label: item.pms,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [stats?.pmsBreakdown],
  );

  const allHotelValues = useMemo(() => hotelOptions.map(option => option.value), [hotelOptions]);
  const allPmsValues = useMemo(() => pmsOptions.map(option => option.value), [pmsOptions]);

  const hotelCodeFilter = buildMultiSelectFilterParam(selectedHotelCodes, allHotelValues);
  const pmsFilter = buildMultiSelectFilterParam(selectedPms, allPmsValues);

  const resolveHotelDisplayName = useCallback(
    (hotelCode: string) => getHotelDisplayName(hotelCode, hotelNames),
    [hotelNames],
  );

  const resolveHotelCodeList = useCallback(
    (hotelCodes: string[]) => formatHotelCodeList(hotelCodes, hotelNames),
    [hotelNames],
  );

  return {
    stats,
    statsError,
    hotelNames,
    hotelOptions,
    pmsOptions,
    selectedHotelCodes,
    selectedPms,
    setSelectedHotelCodes,
    setSelectedPms,
    hotelCodeFilter,
    pmsFilter,
    getHotelDisplayName: resolveHotelDisplayName,
    formatHotelCodeList: resolveHotelCodeList,
    ensureHotelNames,
    reloadFilterOptions,
  };
}
